import { memo } from 'react';
import { Box, Flex, Input } from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import { Control, UseFormRegister, UseFormWatch, UseFormSetValue, RegisterOptions } from 'react-hook-form';
import { ITimezoneOption } from 'react-timezone-select';

import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';
import { Field } from '@/components/ui/field';
import { FormValues } from '../hooks/useSessionForm';
import { TimezoneDropdown } from './TimezoneDropdown';

const ErrorMessage = ({ message }: { message?: string }) =>
  message ? (
    <Box fontSize="14px" color="error.700" role="alert">
      {message}
    </Box>
  ) : null;

const TimeLabel = ({ children }: { children: React.ReactNode }) => (
  <Box
    w="100%"
    border="1px solid"
    borderRight="none"
    borderLeftRadius="8px"
    borderColor="neutrals.200"
    p="8px 16px"
    fontSize="16px"
  >
    {children}
  </Box>
);

interface DeadlineSectionProps {
  control: Control<FormValues>;
  register: UseFormRegister<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  errors: Record<string, { message?: string }>;
  isSubmitting: boolean;
  isLoadingCandidate: boolean;
  options: { value: string; label: string }[];
  parseTimezone: (timezone: string) => ITimezoneOption;
  validationRules: Record<string, RegisterOptions<FormValues>>;
}

export const DeadlineSection: React.FC<DeadlineSectionProps> = memo(
  ({
    control,
    register,
    watch,
    setValue,
    errors,
    isSubmitting,
    isLoadingCandidate,
    options,
    parseTimezone,
    validationRules
  }) => (
    <Flex gap="16px" justifyContent="flex-start">
      <Field
        flex="1"
        label={<RequiredFieldLabel fontWeight="semibold">Deadline</RequiredFieldLabel>}
        invalid={!!errors.deadline || !!errors.deadline_time}
        errorText={
          <ErrorMessage message={errors.deadline?.message || errors.deadline_time?.message} />
        }
      >
        <Flex w="100%" gap="8px">
          <Box>
            <DatePicker
              disabled={isSubmitting || isLoadingCandidate}
              selected={watch('deadline')}
              onChange={(date: Date | null) => date && setValue('deadline', date)}
              placeholderText="Select date"
              dateFormat="MMMM d, yyyy"
              excludeDateIntervals={[
                {
                  start: new Date(0),
                  end: new Date(new Date().setDate(new Date().getDate() - 1))
                }
              ]}
              autoFocus={false}
              customInput={
                <Input
                  cursor="pointer"
                  _disabled={{
                    bg: 'neutrals.300'
                  }}
                  size="lg"
                  borderRadius="8px"
                  autoFocus={false}
                  placeholder="Select date"
                  aria-label="Deadline"
                  {...register('deadline', validationRules.deadline)}
                />
              }
            />
          </Box>
          <Flex>
            <TimeLabel>Time</TimeLabel>
            <Box w="100%">
              <DatePicker
                disabled={isSubmitting || isLoadingCandidate}
                selected={watch('deadline_time')}
                onChange={(date: Date | null) => {
                  if (date) {
                    setValue('deadline_time', date);
                  }
                }}
                showTimeSelect
                showTimeSelectOnly
                placeholderText="Select time"
                dateFormat="h:mm aa"
                timeIntervals={15}
                timeCaption="Time"
                minTime={new Date(new Date().setHours(0, 0, 0, 0))}
                maxTime={new Date(new Date().setHours(23, 59, 0, 0))}
                autoFocus={false}
                customInput={
                  <Input
                    cursor="pointer"
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    w="120px"
                    size="lg"
                    borderRightRadius="8px"
                    borderLeftRadius="0px"
                    autoFocus={false}
                    placeholder="Select time"
                    aria-label="Meeting start time"
                    {...register('deadline_time', validationRules.deadline_time)}
                  />
                }
              />
            </Box>
          </Flex>
        </Flex>
      </Field>

      <TimezoneDropdown
        control={control}
        error={errors.timezone}
        isSubmitting={isSubmitting}
        isLoadingCandidate={isLoadingCandidate}
        options={options}
        parseTimezone={parseTimezone}
      />
    </Flex>
  )
);

DeadlineSection.displayName = 'DeadlineSection';
